import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { APIURL } from "../../../Config";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField } from "@mui/material";
import { DownloadTableExcel } from "react-export-table-to-excel";
import Pagination from "../../Pagination/Pagination";
import LoanDetails from "../../LoanDetails";

function Funnel() {
  const token = localStorage.getItem("token");
  const tableRef = useRef(null);
  const tableRef1 = useRef(null);
  const tableRef2 = useRef(null);

  // State for tabs and general data
  const [activeTab, setActiveTab] = useState("lenders");
  const [isLoading, setIsLoading] = useState(false);
  const [showPrePage, setShowPrePage] = useState(100);
  const [pagination, setPagination] = useState({ start: 0, end: 100 });
  const [showSearch, setShowSearch] = useState(false);
  const [lid, setLid] = useState("");

  // State for dates and filters
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // Date state for lenders tab (first tab)
  const [lendersDateRange, setLendersDateRange] = useState({
    from: moment(firstDayOfMonth).format("YYYY-MM-DD"),
    to: moment(today).format("YYYY-MM-DD"),
  });
  const [selectedMonthRange, setSelectedMonthRange] = useState("0");

  // Date state for other tabs
  const [rejectedDateRange, setRejectedDateRange] = useState({
    from: moment(firstDayOfMonth).format("YYYY-MM-DD"),
    to: moment(today).format("YYYY-MM-DD"),
  });
  const [loginDumpDateRange, setLoginDumpDateRange] = useState({
    from: moment(firstDayOfMonth).format("YYYY-MM-DD"),
    to: moment(today).format("YYYY-MM-DD"),
  });

  // State for data
  const [list, setClist] = useState([]);
  const [option, setOption] = useState("CTL");
  const [row, setRow] = useState([]);
  const [lenderData, setLenderData] = useState({
    CTLPER: [],
    CTL: [],
    PLSTD: [],
  });
  const [selectedLender, setSelectedLender] = useState("CTL");

  // Month range options for lenders tab
  const monthRangeOptions = [
    { value: "0", label: "Current Month" },
    { value: "1", label: "Last 1 Month" },
    { value: "2", label: "Last 2 Months" },
  ];

  // Set date range based on selected option (for lenders tab only)
  const handleMonthRangeChange = (value) => {
    setSelectedMonthRange(value);
    const today = new Date();

    switch (value) {
      case "current_month":
        setLendersDateRange({
          from: moment(
            new Date(today.getFullYear(), today.getMonth(), 1)
          ).format("YYYY-MM-DD"),
          to: moment(today).format("YYYY-MM-DD"),
        });
        break;
      case "last_1_month":
        setLendersDateRange({
          from: moment(
            new Date(today.getFullYear(), today.getMonth() - 1, 1)
          ).format("YYYY-MM-DD"),
          to: moment(new Date(today.getFullYear(), today.getMonth(), 0)).format(
            "YYYY-MM-DD"
          ),
        });
        break;
      case "last_2_months":
        setLendersDateRange({
          from: moment(
            new Date(today.getFullYear(), today.getMonth() - 2, 1)
          ).format("YYYY-MM-DD"),
          to: moment(today).format("YYYY-MM-DD"),
        });
        break;
      case "last_3_months":
        setLendersDateRange({
          from: moment(
            new Date(today.getFullYear(), today.getMonth() - 3, 1)
          ).format("YYYY-MM-DD"),
          to: moment(today).format("YYYY-MM-DD"),
        });
        break;
      case "custom":
        // Keep current dates for custom selection
        break;
      default:
        break;
    }
  };

  // Fetch data functions
  const fetchData = async () => {
    setIsLoading(true);
    try {
      switch (activeTab) {
        case "rejected":
          const rejectedRes = await axios.request(
            `${APIURL}/rejectionReasonscsa/${rejectedDateRange.from}/${rejectedDateRange.to}/${option}`,
            {
              method: "GET",
              headers: {
                Authentication: token,
                "Content-Type": "application/json",
              },
            }
          );
          setRow(rejectedRes?.data);
          break;
        case "lenders":
          const [ctlPerRes, ctlRes, plstdRes] = await Promise.all([
            axios.get(`${APIURL}/getFunnelRepoCTLPER/${selectedMonthRange}`),
            axios.get(`${APIURL}/getFunnelRepoCTL/${selectedMonthRange}`),
            axios.get(`${APIURL}/getFunnelRepoPLSTD/${selectedMonthRange}`),
          ]);
          setLenderData({
            CTLPER: ctlPerRes.data,
            CTL: ctlRes.data,
            PLSTD: plstdRes.data,
          });
          break;
        case "loginDump":
          const loginDumpRes = await axios.request(
            `${APIURL}/rejectionReasonscsa/${loginDumpDateRange.from}/${loginDumpDateRange.to}/${option}`,
            {
              method: "GET",
              headers: {
                Authentication: token,
                "Content-Type": "application/json",
              },
            }
          );
          setRow(loginDumpRes?.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${activeTab} data:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const getClist = async () => {
    try {
      const response = await axios.request(`${APIURL}/cList`, {
        method: "GET",
        headers: {
          Authentication: token,
          "Content-Type": "application/json",
        },
      });
      setClist(response?.data);
    } catch (error) {
      console.error("Error fetching client list:", error);
    }
  };

  // Handle tab change
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // Reset to first page when changing tabs
    setPagination({ start: 0, end: showPrePage });
  };

  // Handle pagination change
  const onPaginationChange = (start, end) => {
    setPagination({ start, end });
  };

  // Initialize component
  useEffect(() => {
    getClist();
    fetchData();
  }, [activeTab]);

  // Fetch data when date range or option changes
  useEffect(() => {
    fetchData();
  }, [
    lendersDateRange,
    rejectedDateRange,
    loginDumpDateRange,
    option,
    selectedLender,
  ]);

  // Common table columns for rejected funnel
  const rejectedFunnelColumns = [
    { key: "appclientid", label: "App Client ID" },
    { key: "name", label: "Name" },
    { key: "contactno", label: "Mobile No" },
    {
      key: "entrydate",
      label: "Entry Date",
      format: (value) =>
        value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "-",
    },
    { key: "emailveryfied", label: "Email veryfied" },
    { key: "aadhaarid", label: "Aadhaar" },
    { key: "pancard", label: "PAN" },
    { key: "applicationstatus", label: "Application Status" },
    { key: "pincode", label: "Pincode" },
    { key: "code", label: "Source" },
    { key: "rejectedreason", label: "Rejected Reason" },
  ];

  // Common table columns for lender reports
  const lenderReportColumns = [
    { key: "appClientId", label: "App Client ID" },
    { key: "loanId", label: "Loan ID" },
    {
      key: "entryDate",
      label: "Entry Date",
      format: (value) =>
        value ? moment(value).format("DD-MM-YYYY HH:mm:ss") : "-",
    },
    { key: "clientName", label: "Client Name" },
    { key: "lender", label: "Lender" },
    { key: "mobileNo", label: "Mobile No" },
    // { key: "selectScheme", label: "Select Scheme" },
    { key: "mobileVerify", label: "Mobile Verify" },
    { key: "quickProfiles", label: "Quick Profiles" },
    { key: "kyc", label: "KYC" },
    { key: "emailveryfied", label: "Email veryfied" },

    { key: "termAndCondition", label: "T&C" },
    { key: "aadhaarNumberUpdate", label: "Aadhaar Update" },

    { key: "disburse", label: "Disburse" },

    { key: "rejectReason", label: "Reject Reason" },
  ];

  return (
    <div className="p-3">
      <div>
        <ul className="nav nav-tabs" id="myTab" role="tablist">
          {["lenders", "rejected", "loginDump"].map((tab) => (
            <li key={tab} className="nav-item" role="presentation">
              <button
                className={`nav-link tab-item ${
                  activeTab === tab ? "active" : ""
                }`}
                id={`${tab}-tab`}
                data-bs-toggle="tab"
                data-bs-target={`#${tab}`}
                type="button"
                role="tab"
                aria-controls={tab}
                aria-selected={activeTab === tab}
                onClick={() => handleTabClick(tab)}
              >
                {tab === "lenders"
                  ? "Funnel Reports"
                  : tab === "rejected"
                  ? "Rejected Funnel"
                  : "Login Dump"}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content" id="myTabContent">
          {/* Rejected Funnel Tab */}
          <div
            className={`tab-pane fade ${
              activeTab === "rejected" ? "show active" : ""
            }`}
            id="rejected"
            role="tabpanel"
            aria-labelledby="rejected-tab"
          >
            <div className="pt-3">
              <div className="card">
                <div className="p-3 pt-4">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                      <DownloadTableExcel
                        filename="RejectedFunnelReport"
                        sheet="users"
                        currentTableRef={tableRef.current}
                      >
                        <button
                          className="btn btn-success"
                          style={{ width: "120px", background: "#66a725" }}
                        >
                          Download
                        </button>
                      </DownloadTableExcel>
                    </div>

                    <div className="d-flex flex-column flex-md-row align-items-center">
                      <div className="mb-2 mb-md-0 mr-md-2">
                        <select
                          className="form-select"
                          value={option}
                          onChange={(e) => setOption(e.target.value)}
                        >
                          {list.map((item, index) => (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-2 mb-md-0 mr-md-2">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="From Date"
                            value={new Date(rejectedDateRange.from)}
                            onChange={(newValue) => {
                              setRejectedDateRange((prev) => ({
                                ...prev,
                                from: moment(newValue).format("YYYY-MM-DD"),
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" />
                            )}
                            maxDate={new Date(rejectedDateRange.to)}
                          />
                        </LocalizationProvider>
                      </div>

                      <div className="mb-2 mb-md-0 mr-md-2">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="To Date"
                            value={new Date(rejectedDateRange.to)}
                            onChange={(newValue) => {
                              setRejectedDateRange((prev) => ({
                                ...prev,
                                to: moment(newValue).format("YYYY-MM-DD"),
                              }));
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" />
                            )}
                            minDate={new Date(rejectedDateRange.from)}
                            maxDate={new Date()}
                          />
                        </LocalizationProvider>
                      </div>

                      <button
                        className="btn btn-success"
                        style={{ background: "#66a725", width: "100px" }}
                        onClick={fetchData}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div
                            className="spinner-border text-success"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          "Search"
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-striped" ref={tableRef}>
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          {rejectedFunnelColumns.map((col) => (
                            <th key={col.key}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td
                              colSpan={rejectedFunnelColumns.length + 1}
                              className="text-center"
                            >
                              <div
                                className="spinner-border text-success"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          row
                            ?.slice(pagination.start, pagination.end)
                            .map((item, index) => (
                              <tr key={index}>
                                <td>{index + 1 + pagination.start}</td>
                                {rejectedFunnelColumns.map((col) => (
                                  <td key={col.key}>
                                    {col.format
                                      ? col.format(item[col.key])
                                      : item[col.key] || "-"}
                                  </td>
                                ))}
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {row && row?.length > showPrePage && (
                    <Pagination
                      showPerPage={showPrePage}
                      onPaginationChange={onPaginationChange}
                      total={row?.length}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Lender Reports Tab (with month range selector) */}
          <div
            className={`tab-pane fade ${
              activeTab === "lenders" ? "show active" : ""
            }`}
            id="lenders"
            role="tabpanel"
            aria-labelledby="lenders-tab"
          >
            <div className="pt-3">
              <div className="card">
                <div className="p-3 pt-4">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                      <DownloadTableExcel
                        filename={`LenderReport-${selectedLender}`}
                        sheet="users"
                        currentTableRef={tableRef1.current}
                      >
                        <button
                          className="btn btn-success"
                          style={{ width: "120px", background: "#66a725" }}
                        >
                          Download
                        </button>
                      </DownloadTableExcel>
                    </div>

                    <div className="d-flex flex-column flex-md-row align-items-center">
                      <div className="mb-2 mb-md-0 mr-md-2">
                        <select
                          className="form-select"
                          value={option}
                          onChange={(e) => setOption(e.target.value)}
                        >
                          {list.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-2 mb-md-0 mr-md-2">
                        <select
                          className="form-select"
                          value={selectedMonthRange}
                          onChange={(e) =>
                            setSelectedMonthRange(e.target.value)
                          }
                        >
                          {monthRangeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedMonthRange === "custom" && (
                        <>
                          <div className="mb-2 mb-md-0 mr-md-2">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="From Date"
                                value={new Date(lendersDateRange.from)}
                                onChange={(newValue) => {
                                  setLendersDateRange((prev) => ({
                                    ...prev,
                                    from: moment(newValue).format("YYYY-MM-DD"),
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} size="small" />
                                )}
                                maxDate={new Date(lendersDateRange.to)}
                              />
                            </LocalizationProvider>
                          </div>

                          <div className="mb-2 mb-md-0 mr-md-2">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="To Date"
                                value={new Date(lendersDateRange.to)}
                                onChange={(newValue) => {
                                  setLendersDateRange((prev) => ({
                                    ...prev,
                                    to: moment(newValue).format("YYYY-MM-DD"),
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} size="small" />
                                )}
                                minDate={new Date(lendersDateRange.from)}
                                maxDate={new Date()}
                              />
                            </LocalizationProvider>
                          </div>
                        </>
                      )}

                      <button
                        className="btn btn-success"
                        style={{ background: "#66a725" }}
                        onClick={fetchData}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div
                            className="spinner-border text-success"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          "Refresh"
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-striped" ref={tableRef1}>
                      <thead>
                        <tr>
                          <th>Sr. No</th>
                          {lenderReportColumns.map((col) => (
                            <th key={col.key}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td
                              colSpan={lenderReportColumns.length + 1}
                              className="text-center"
                            >
                              <div
                                className="spinner-border text-success"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          lenderData[selectedLender] &&
                          lenderData[selectedLender]?.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1 + pagination.start}</td>
                              {lenderReportColumns.map((col) => (
                                <td key={col.key}>
                                  {col.format
                                    ? col.format(item[col.key])
                                    : item[col.key] || "-"}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Login Dump Tab */}
          <div
            className={`tab-pane fade ${
              activeTab === "loginDump" ? "show active" : ""
            }`}
            id="loginDump"
            role="tabpanel"
            aria-labelledby="loginDump-tab"
          >
            <div className="pt-3">
              <div className="card">
                <div className="p-3 pt-4">
                  {showSearch ? (
                    <>
                      <div className="p-3 d-flex justify-content-start mt-0">
                        <button
                          className="btn btn-secondary"
                          style={{ width: "100px" }}
                          onClick={() => setShowSearch(false)}
                        >
                          Back
                        </button>
                      </div>
                      <div className="p-3 pt-2">
                        <LoanDetails lid={lid} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                        <div className="d-flex align-items-center mb-3 mb-md-0">
                          <DownloadTableExcel
                            filename="Login Dump"
                            sheet="users"
                            currentTableRef={tableRef2.current}
                          >
                            <button
                              className="btn btn-success"
                              style={{ width: "120px", background: "#66a725" }}
                            >
                              Download
                            </button>
                          </DownloadTableExcel>
                        </div>

                        <div className="d-flex flex-column flex-md-row align-items-center">
                          <div className="mb-2 mb-md-0 mr-md-2">
                            <select
                              className="form-select"
                              value={option}
                              onChange={(e) => setOption(e.target.value)}
                            >
                              {list.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-2 mb-md-0 mr-md-2">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="From Date"
                                value={new Date(loginDumpDateRange.from)}
                                onChange={(newValue) => {
                                  setLoginDumpDateRange((prev) => ({
                                    ...prev,
                                    from: moment(newValue).format("YYYY-MM-DD"),
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} size="small" />
                                )}
                                maxDate={new Date(loginDumpDateRange.to)}
                              />
                            </LocalizationProvider>
                          </div>

                          <div className="mb-2 mb-md-0 mr-md-2">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="To Date"
                                value={new Date(loginDumpDateRange.to)}
                                onChange={(newValue) => {
                                  setLoginDumpDateRange((prev) => ({
                                    ...prev,
                                    to: moment(newValue).format("YYYY-MM-DD"),
                                  }));
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} size="small" />
                                )}
                                minDate={new Date(loginDumpDateRange.from)}
                                maxDate={new Date()}
                              />
                            </LocalizationProvider>
                          </div>

                          <button
                            className="btn btn-success"
                            style={{ background: "#66a725", maxWidth: "100px" }}
                            onClick={fetchData}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <div
                                className="spinner-border text-success"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              "Submit"
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="table-responsive mt-3">
                        <table className="table table-striped" ref={tableRef2}>
                          <thead>
                            <tr>
                              <th>Sr. No</th>
                              <th>Entry Date</th>
                              <th>Loan Id</th>
                              <th>Contact No</th>
                              <th>Name</th>
                              <th>App client id</th>
                              <th>Application Status</th>
                              <th>Rejected Reason</th>
                              <th>Pincode</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan="9" className="text-center">
                                  <div
                                    className="spinner-border text-success"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              row &&
                              row.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1 + pagination.start}</td>
                                  <td>
                                    {item.entrydate
                                      ? moment(item.entrydate).format(
                                          "DD-MM-YYYY"
                                        )
                                      : "-"}
                                  </td>
                                  <td
                                    style={{
                                      color: item.loanId ? "blue" : "inherit",
                                      cursor: item.loanId
                                        ? "pointer"
                                        : "default",
                                    }}
                                    onClick={() => {
                                      if (item.loanId) {
                                        setLid(item.loanId);
                                        setShowSearch(true);
                                      }
                                    }}
                                  >
                                    {item.loanId || "-"}
                                  </td>
                                  <td>{item.contactno || "-"}</td>
                                  <td>{item.name || "-"}</td>
                                  <td>{item.appclientid || "-"}</td>
                                  <td>{item.applicationstatus || "-"}</td>
                                  <td>{item.rejectedreason || "-"}</td>
                                  <td>{item.pincode || "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {!showSearch && row?.length > 0 && (
                    <Pagination
                      showPerPage={showPrePage}
                      onPaginationChange={onPaginationChange}
                      total={row?.length}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Funnel;
